import { formatDistance } from "@/components/common/settings-context/distance-unit-utils";
import {
  useDistanceUnit,
  useSettingsContext,
} from "@/components/common/settings-context/settings-context";
import {
  useWaypointAltitudeRange,
  useWaypointDateRange,
} from "@/modes/mode-data-context";
import { WaypointsColoringOptions } from "@/store/view-options/view-options-slice";
import {
  WAYPOINTS_ALTITUDE_GRADIENT,
  WAYPOINTS_DATE_GRADIENT,
} from "@/utils/waypoints-color-gradient";
import {
  BuildingElevationIcon,
  ColorBar,
  ColorString,
  convertToDateString,
  FaroTooltip,
  grey,
  TimeDistanceIcon,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Stack, SxProps } from "@mui/system";
import { DateTime } from "luxon";
import { ReactNode, useCallback, useState, type JSX } from "react";

type AppColorBarProps = {
  /** The style applied to the stack containing the color bar */
  sx?: SxProps;
};

/** @returns The vertical color bar used to colorize the waypoints on the sheet */
export function AppColorBar({ sx }: AppColorBarProps): JSX.Element | null {
  const waypointsAltitudeRange = useWaypointAltitudeRange();
  const waypointsDateRange = useWaypointDateRange();

  const { waypointsColoring } = useSettingsContext();

  const isElevationAvailable =
    waypointsColoring === WaypointsColoringOptions.byElevation &&
    !!waypointsAltitudeRange;
  const isCaptureDateAvailable =
    waypointsColoring === WaypointsColoringOptions.byCaptureDate &&
    !!waypointsDateRange;

  const distanceUnit = useDistanceUnit();

  const valueToElevation = useCallback(
    (value: number | undefined): ReactNode => {
      if (!value || !waypointsAltitudeRange) return "";
      const elevation =
        waypointsAltitudeRange.lowest +
        value *
          (waypointsAltitudeRange.highest - waypointsAltitudeRange.lowest);
      return (
        <>
          Elevation:{" "}
          <TranslateVar name="elevation">
            {formatDistance(elevation, distanceUnit)}
          </TranslateVar>
        </>
      );
    },
    [waypointsAltitudeRange, distanceUnit],
  );

  const valueToDate = useCallback(
    (value: number | undefined): ReactNode => {
      if (!value || !waypointsDateRange) return "";
      const date = DateTime.fromMillis(
        waypointsDateRange.lowest +
          value * (waypointsDateRange.highest - waypointsDateRange.lowest),
      ).toISODate();
      const dateString = date ? convertToDateString(date) : "";
      return (
        <>
          Capture date:{" "}
          <TranslateVar name="captureDate">{dateString}</TranslateVar>
        </>
      );
    },
    [waypointsDateRange],
  );

  const tooltipFnc = isElevationAvailable ? valueToElevation : valueToDate;

  const [colorbarPoint, setColorbarPoint] = useState<
    ColorAndValue | undefined
  >();

  const onColorbarHovered = useCallback(
    (color: ColorString | undefined, normalizedValue: number) => {
      if (color) {
        setColorbarPoint({ color, value: normalizedValue });
      }
    },
    [],
  );

  if (!isElevationAvailable && !isCaptureDateAvailable) {
    return null;
  }

  return (
    <Stack sx={[{ gap: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {isElevationAvailable && (
        <FaroTooltip title="Colorize by elevation" placement="left">
          <BuildingElevationIcon
            sx={{ width: "16px", height: "16px", color: grey[100] }}
          />
        </FaroTooltip>
      )}
      {isCaptureDateAvailable && (
        <FaroTooltip title="Colorize by capture date" placement="left">
          <TimeDistanceIcon
            sx={{ width: "16px", height: "16px", color: grey[100] }}
          />
        </FaroTooltip>
      )}
      <FaroTooltip
        // The line below is needed to keep the colorbar using the full height
        sx={{ height: "100%" }}
        icon={
          <Stack
            sx={{
              p: 1,
              borderRadius: 2,
              outline: `1px solid ${grey[0]}33`,
              backgroundColor: colorbarPoint?.color,
            }}
          />
        }
        title={tooltipFnc(colorbarPoint?.value)}
        placement="left"
      >
        <ColorBar
          direction="vertical"
          colors={
            isElevationAvailable
              ? WAYPOINTS_ALTITUDE_GRADIENT
              : WAYPOINTS_DATE_GRADIENT
          }
          sx={{ height: "100%" }}
          onPointerMove={onColorbarHovered}
          onPointerEnter={onColorbarHovered}
        />
      </FaroTooltip>
    </Stack>
  );
}

type ColorAndValue = {
  color: ColorString;
  value: number;
};
